import { Injectable } from '@angular/core';
import { utc } from 'moment';
import { TranslateService } from '@ngx-translate/core';
import {
  MediaBlockReason,
  TwilioMessage,
  TwilioMessageMedia,
  TwilioMessageStatus,
} from '@nimbus-global/features/chat/models/chat-twilio.model';
import {
  AuthorType,
  ChatMediaKind,
  IChat,
  IChatCallerInfo,
  IChatMessage,
  IChatMessageMedia,
} from '@nimbus-global/features/chat/models/chat.model';
import { TranslationKeys } from '@nimbus-library/features/localization/models/enums/translation-keys.enum';
import { PhoneNumberService } from '@nimbus-library/features/shared/services/helper/phone-number.service';

@Injectable({ providedIn: 'root' })
export class ToChatMessageMapper {
  public constructor(
    private readonly translationService: TranslateService,
    private readonly phoneNumberService: PhoneNumberService,
  ) {}

  public twilioToChatMessagesMapper(twilioMessages: TwilioMessage[], callerInfo: IChatCallerInfo): IChat {
    // Handle case where twilioMessages might be undefined or null
    if (!twilioMessages || !Array.isArray(twilioMessages)) {
      return { messages: [] };
    }
    const messages = twilioMessages.map((twilioMessage, messageIndex) =>
      this.mapTwilioMessageToChatMessage(twilioMessage, callerInfo, messageIndex, twilioMessages[messageIndex - 1]?.dateCreated ?? null),
    );
    return { messages: messages.sort((a, b) => a.index - b.index) };
  }

  public mapTwilioMessageToChatMessage(
    twilioMessage: TwilioMessage,
    callerInfo: IChatCallerInfo,
    messageIndex: number,
    prevMessageDate: string,
    updatedStatus?: TwilioMessageStatus,
  ): IChatMessage {
    const resolvedIndex = twilioMessage.index ?? messageIndex;
    const resolvedAuthorType = this.getAuthorType(twilioMessage.author, callerInfo.id);
    const media = this.mapMedia(twilioMessage.media, resolvedIndex, resolvedAuthorType);
    const hasMedia = media.length > 0;
    const authorType: AuthorType = twilioMessage.body || hasMedia ? resolvedAuthorType : AuthorType.System;

    return {
      id: twilioMessage.messageSid || `get_message_${resolvedIndex}_${Date.now()}`,
      author: this.getAuthorDisplayName(authorType, twilioMessage.author, callerInfo.displayName),
      authorType,
      showAuthorInfo: false,
      dateTime: twilioMessage.dateCreated ? utc(twilioMessage.dateCreated).toISOString() : utc().toISOString(),
      index: resolvedIndex,
      content:
        twilioMessage.body || (hasMedia ? '' : this.translationService.instant(TranslationKeys.ChatWidgetMessageFileSharingNotSupported)),
      media: hasMedia ? media : undefined,
      status:
        authorType === AuthorType.Agent
          ? (twilioMessage.deliveryState as TwilioMessageStatus) || updatedStatus || TwilioMessageStatus.None
          : null,
      failureReason: twilioMessage.failureReason ?? null,
      isFirstOfTheDay: this.isFirstMessageOfTheDay(twilioMessage.dateCreated, prevMessageDate),
    };
  }

  public getAuthorType(author: string, callerId: string): AuthorType {
    if (author === 'System') {
      return AuthorType.System;
    }
    if (author.startsWith('whatsapp:') || this.isPhoneNumber(author)) {
      return AuthorType.Customer;
    }
    if (author === callerId) {
      return AuthorType.Agent;
    }
    return AuthorType.Agent;
  }

  public getAuthorDisplayName(authorType: AuthorType, author: string, displayName: string): string {
    switch (authorType) {
      case AuthorType.System:
        return this.translationService.instant(TranslationKeys.ChatWidgetMessageAuthorNimbusSystem);
      case AuthorType.Agent:
        return this.translationService.instant(TranslationKeys.ChatWidgetMessageAuthorYou);
      default:
        return this.getDisplayAuthor(author, displayName);
    }
  }

  private mapMedia(media: TwilioMessageMedia[] | null | undefined, messageIndex: number, authorType: AuthorType): IChatMessageMedia[] {
    if (!media?.length) {
      return [];
    }
    return media
      .filter((item) => !!item?.url || item?.isBlocked)
      .map((item, mediaIndex) => {
        const kind = this.getMediaKind(item.contentType);
        return {
          url: item.url,
          contentType: item.contentType,
          filename: this.resolveMediaFilename(item.filename, item.contentType, kind, authorType, messageIndex, mediaIndex),
          size: item.size,
          kind,
          blockReason: item.blockReason ?? MediaBlockReason.None,
        };
      });
  }

  // Twilio gives some attachments a generic "File.<ext>" name when the sender's client didn't
  // provide one, and some of our own media responses come back with no filename at all -
  // replace both placeholders with a name that's still unique and traceable to who sent it.
  private readonly genericFilenamePattern = /^file\.[a-z0-9]+$/i;

  private resolveMediaFilename(
    filename: string | null,
    contentType: string | null,
    kind: ChatMediaKind,
    authorType: AuthorType,
    messageIndex: number,
    mediaIndex: number,
  ): string {
    if (filename && !this.genericFilenamePattern.test(filename)) {
      return filename;
    }
    const displayMessageIndex = messageIndex + 1;
    const displayMediaIndex = mediaIndex + 1;
    const authorPrefix = authorType === AuthorType.Customer ? 'Customer' : 'Agent';
    return `${authorPrefix}-${kind.toLowerCase()}_${displayMessageIndex}_${displayMediaIndex}.${this.getExtension(filename, contentType)}`;
  }

  private getExtension(filename: string | null, contentType: string | null): string {
    const filenameMatch = /\.([a-zA-Z0-9]+)$/.exec(filename ?? '');
    if (filenameMatch) {
      return filenameMatch[1].toLowerCase();
    }
    const defaultExtension = 'bin';
    const subtype = (contentType ?? '').split('/')[1] ?? '';
    return subtype.split('+')[0].toLowerCase() || defaultExtension;
  }

  // Browsers (Chrome, Firefox, Edge) can't decode these inline, even though the server reports them as image/audio/video -
  // render them as a generic file tile instead so they don't hit a permanent, unretryable load error.
  private readonly unsupportedInlineImageTypes = ['image/tiff', 'image/heic', 'image/heic-sequence', 'image/heif', 'image/heif-sequence'];
  private readonly unsupportedInlineAudioTypes = [
    'audio/ac3',
    'audio/amr',
    'audio/amr-nb',
    'audio/3gpp',
    'audio/3gpp2',
    'audio/vnd.rn-realaudio',
    'audio/x-pn-realaudio',
  ];
  private readonly unsupportedInlineVideoTypes = ['video/quicktime', 'video/mpeg', 'video/3gpp', 'video/3gpp2'];

  private getMediaKind(contentType: string | null): ChatMediaKind {
    const type = (contentType ?? '').toLowerCase();
    if (type.startsWith('image/') && !this.unsupportedInlineImageTypes.includes(type)) {
      return ChatMediaKind.Image;
    }
    if (type.startsWith('audio/') && !this.unsupportedInlineAudioTypes.includes(type)) {
      return ChatMediaKind.Audio;
    }
    if (type.startsWith('video/') && !this.unsupportedInlineVideoTypes.includes(type)) {
      return ChatMediaKind.Video;
    }
    return ChatMediaKind.File;
  }

  private isPhoneNumber(author: string): boolean {
    const phoneInfo = this.phoneNumberService.normalizeNumber(author);
    return phoneInfo.isValid;
  }

  private isFirstMessageOfTheDay(messageDate: string, prevMessageDate: string): boolean {
    if (!messageDate || !prevMessageDate) {
      return true;
    }

    const currentLocal = utc(messageDate).local();
    const prevLocal = utc(prevMessageDate).local();
    return !currentLocal.isSame(prevLocal, 'day');
  }

  private getDisplayAuthor(author: string, displayName: string): string {
    if (displayName) {
      return displayName;
    }
    if (author.startsWith('whatsapp:')) {
      return author.substring('whatsapp:'.length);
    }
    return author;
  }
}
